/* ============================================================================
   Route + navigation registry.
   Feature modules self-register their routes by calling registerRoute() from a
   `<name>.routes.tsx` file. App.tsx eagerly imports every *.routes.tsx via
   import.meta.glob, so adding a module NEVER requires editing this file or any
   other shared/spine file.
   ============================================================================ */

import type { ReactElement } from 'react';
import type { Role } from '../lib/auth';

export interface NavSpec {
  /** Label shown in the app-bar nav. */
  label: string;
  /** Optional grouping section (e.g. "Admin"). Ungrouped items render first. */
  section?: string;
  /** Lower numbers sort earlier within a section. Default 100. */
  order?: number;
  /** If set, only these roles see the nav item (route still guarded separately). */
  roles?: Role[] | string[];
  /** Optional short icon glyph/emoji rendered before the label. */
  icon?: string;
}

export interface RouteSpec {
  /** react-router path, e.g. "/requests" or "/requests/:id". */
  path: string;
  /** The element to render for this route. */
  element: ReactElement;
  /** If present, the route appears in the nav. */
  nav?: NavSpec;
  /** Roles allowed to access the route (enforced by AppShell). Empty = any authenticated user. */
  roles?: Role[] | string[];
  /** When true, render outside the authenticated shell (e.g. the login page). */
  publicRoute?: boolean;
}

const routes: RouteSpec[] = [];

export function registerRoute(spec: RouteSpec): void {
  if (routes.some((r) => r.path === spec.path)) {
    // Duplicate path — last registration wins but warn loudly in dev.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[registry] duplicate route path "${spec.path}" — overriding.`);
    }
    const idx = routes.findIndex((r) => r.path === spec.path);
    routes.splice(idx, 1);
  }
  routes.push(spec);
}

export function getRoutes(): RouteSpec[] {
  return routes.slice();
}

export interface NavItem extends NavSpec {
  path: string;
  roles?: Role[] | string[];
}

/** Nav items, sorted by (section, order, label). Section-less items sort first. */
export function getNav(): NavItem[] {
  return routes
    .filter((r) => r.nav && !r.publicRoute)
    .map((r) => ({ ...(r.nav as NavSpec), path: r.path, roles: r.nav!.roles ?? r.roles }))
    .sort((a, b) => {
      const sa = a.section ?? '';
      const sb = b.section ?? '';
      if (sa !== sb) return sa.localeCompare(sb);
      const oa = a.order ?? 100;
      const ob = b.order ?? 100;
      if (oa !== ob) return oa - ob;
      return a.label.localeCompare(b.label);
    });
}
